/**
 * Property API Client
 *
 * TypeScript client for interacting with the Property API Worker.
 */

import type {
  MarketsResponse,
  Market,
  MarketZipsResponse,
  PropertiesResponse,
  CrawlStatus,
  CrawlTriggerResponse,
  Buyer,
  BuyerCreateInput,
  BuyersResponse,
  BuyerMatchResponse,
  BuyerStats,
  StatesResponse,
  StateMarketsResponse,
  StateMarketDetailResponse,
  MarketZipsNationalResponse,
  D1PropertyFilters,
  D1PropertiesResponse,
  InvestmentPropertyFilters,
  InvestmentPropertiesResponse,
  InvestmentPropertyState,
} from '../types';

const DEFAULT_BASE_URL = 'https://property-api.aelieve.workers.dev';

class PropertyAPI {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || localStorage.getItem('propertyApiUrl') || DEFAULT_BASE_URL;
  }

  setBaseUrl(url: string): void {
    this.baseUrl = url;
    localStorage.setItem('propertyApiUrl', url);
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}`);
    }

    return data as T;
  }

  // Health check
  async health(): Promise<{ status: string }> {
    return this.request('/api/health');
  }

  // Markets
  async getMarkets(): Promise<MarketsResponse> {
    return this.request('/api/markets');
  }

  async getMarket(marketKey: string): Promise<Market> {
    return this.request(`/api/markets/${marketKey}`);
  }

  async getMarketZips(market: string): Promise<MarketZipsResponse> {
    return this.request(`/api/markets/${market}/zips`);
  }

  async getMarketProperties(market: string): Promise<PropertiesResponse> {
    return this.request(`/api/markets/${market}/properties`);
  }

  // D1-based property search with server-side filtering
  async getProperties(filters: D1PropertyFilters = {}): Promise<D1PropertiesResponse> {
    const params = new URLSearchParams();
    if (filters.market) params.set('market', filters.market);
    if (filters.minPrice) params.set('minPrice', String(filters.minPrice));
    if (filters.maxPrice) params.set('maxPrice', String(filters.maxPrice));
    if (filters.minBeds) params.set('minBeds', String(filters.minBeds));
    if (filters.maxBeds) params.set('maxBeds', String(filters.maxBeds));
    if (filters.minScore) params.set('minScore', String(filters.minScore));
    if (filters.propertyType) params.set('propertyType', filters.propertyType);
    if (filters.status) params.set('status', filters.status);
    if (filters.excludeStale) params.set('excludeStale', 'true');
    if (filters.maxAgeDays) params.set('maxAgeDays', String(filters.maxAgeDays));
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset) params.set('offset', String(filters.offset));
    return this.request(`/api/properties?${params.toString()}`);
  }

  // Investment Properties (from property_investment_view)
  async getInvestmentProperties(
    filters: InvestmentPropertyFilters = {}
  ): Promise<InvestmentPropertiesResponse> {
    const params = new URLSearchParams();
    if (filters.stateCode) params.set('state', filters.stateCode);
    if (filters.city) params.set('city', filters.city);
    if (filters.zipCode) params.set('zip', filters.zipCode);
    if (filters.minPrice) params.set('minPrice', String(filters.minPrice));
    if (filters.maxPrice) params.set('maxPrice', String(filters.maxPrice));
    if (filters.minBeds) params.set('minBeds', String(filters.minBeds));
    if (filters.maxBeds) params.set('maxBeds', String(filters.maxBeds));
    if (filters.minScore) params.set('minScore', String(filters.minScore));
    if (filters.propertyType) params.set('propertyType', filters.propertyType);
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset) params.set('offset', String(filters.offset));
    // Map bounds filtering
    if (filters.north !== undefined) params.set('north', String(filters.north));
    if (filters.south !== undefined) params.set('south', String(filters.south));
    if (filters.east !== undefined) params.set('east', String(filters.east));
    if (filters.west !== undefined) params.set('west', String(filters.west));
    return this.request(`/api/properties/investment?${params.toString()}`);
  }

  async getInvestmentPropertyStates(): Promise<{ states: InvestmentPropertyState[] }> {
    return this.request('/api/properties/investment/states');
  }

  // Get clustered properties for map (server-side clustering)
  async getInvestmentPropertyClusters(
    filters: InvestmentPropertyFilters & { gridSize?: number }
  ): Promise<{
    total: number;
    clusterCount: number;
    gridSize: number;
    clusters: Array<{
      lat: number;
      lng: number;
      count: number;
      avgPrice: number;
      avgScore: number;
      minPrice: number;
      maxPrice: number;
      bounds: { north: number; south: number; east: number; west: number };
    }>;
  }> {
    const params = new URLSearchParams();
    if (filters.north !== undefined) params.set('north', String(filters.north));
    if (filters.south !== undefined) params.set('south', String(filters.south));
    if (filters.east !== undefined) params.set('east', String(filters.east));
    if (filters.west !== undefined) params.set('west', String(filters.west));
    if (filters.gridSize) params.set('gridSize', String(filters.gridSize));
    if (filters.stateCode) params.set('state', filters.stateCode);
    if (filters.minPrice) params.set('minPrice', String(filters.minPrice));
    if (filters.maxPrice) params.set('maxPrice', String(filters.maxPrice));
    if (filters.minBeds) params.set('minBeds', String(filters.minBeds));
    if (filters.minScore) params.set('minScore', String(filters.minScore));
    if (filters.propertyType) params.set('propertyType', filters.propertyType);
    return this.request(`/api/properties/investment/clusters?${params.toString()}`);
  }

  async getMarketZipsNational(market: string): Promise<MarketZipsNationalResponse> {
    return this.request(`/api/markets/${market}/zips/national`);
  }

  // States (drill-down navigation)
  async getStates(): Promise<StatesResponse> {
    return this.request('/api/states');
  }

  async getStateMarkets(stateCode: string): Promise<StateMarketsResponse> {
    return this.request(`/api/states/${stateCode.toUpperCase()}`);
  }

  async getStateMarketDetail(stateCode: string, market: string): Promise<StateMarketDetailResponse> {
    return this.request(`/api/states/${stateCode.toUpperCase()}/markets/${market}`);
  }

  // Vintages
  async getVintages(): Promise<{ vintages: string[]; latest: string }> {
    return this.request('/api/vintages');
  }

  async getVintageSummary(vintage: string): Promise<unknown> {
    return this.request(`/api/vintages/${vintage}/summary`);
  }

  // Crawl operations
  async triggerCrawl(
    market = 'all',
    budget = 300000,
    options?: {
      zipCodes?: string[];
      maxZips?: number;
      crawlType?: 'properties' | 'market_data' | 'full';
      crawlOptions?: {
        location?: string;
        offerTypes?: string[];
        providers?: string[];
        maxResultsPerProvider?: number;
        deduplicateResults?: boolean;
      };
    }
  ): Promise<CrawlTriggerResponse> {
    return this.request('/api/crawl/trigger', {
      method: 'POST',
      body: JSON.stringify({
        market,
        budget,
        zipCodes: options?.zipCodes,
        maxZips: options?.maxZips,
        crawlType: options?.crawlType || 'full',
        crawlOptions: options?.crawlOptions,
      }),
    });
  }

  async getCrawlStatus(): Promise<CrawlStatus> {
    return this.request('/api/crawl/status');
  }

  async cancelCrawlJob(jobId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/crawl/job/${jobId}/cancel`, {
      method: 'POST',
    });
  }

  async getStoredMarkets(): Promise<{ markets: Array<{ market: string; name: string; state_code: string; count: number; crawled_at: string }> }> {
    return this.request('/api/crawl/markets');
  }

  async clearAllCrawls(): Promise<{ message: string; deleted: number }> {
    return this.request('/api/crawl/all', {
      method: 'DELETE',
    });
  }

  // ZIP data
  async getZipStats(): Promise<unknown> {
    return this.request('/api/zips/stats');
  }

  async getZipDetail(zipCode: string): Promise<unknown> {
    return this.request(`/api/zips/${zipCode.padStart(5, '0')}`);
  }

  async searchZips(filters: {
    state?: string;
    metro?: string;
    min_score?: number;
    max_price?: number;
    min_yield?: number;
    limit?: number;
  } = {}): Promise<unknown> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) params.set(key, String(value));
    });
    return this.request(`/api/zips?${params.toString()}`);
  }

  // Buyer operations
  async getBuyers(filters: {
    active?: boolean;
    zip?: string;
    price?: number;
  } = {}): Promise<BuyersResponse> {
    const params = new URLSearchParams();
    if (filters.active !== undefined) params.set('active', String(filters.active));
    if (filters.zip) params.set('zip', filters.zip);
    if (filters.price) params.set('price', String(filters.price));
    return this.request(`/api/buyers?${params.toString()}`);
  }

  async getBuyer(id: string): Promise<Buyer> {
    return this.request(`/api/buyers/${id}`);
  }

  async createBuyer(buyer: BuyerCreateInput): Promise<{ id: string; message: string }> {
    return this.request('/api/buyers', {
      method: 'POST',
      body: JSON.stringify(buyer),
    });
  }

  async updateBuyer(id: string, updates: Partial<BuyerCreateInput>): Promise<{ message: string }> {
    return this.request(`/api/buyers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteBuyer(id: string): Promise<{ message: string }> {
    return this.request(`/api/buyers/${id}`, {
      method: 'DELETE',
    });
  }

  async matchBuyers(property: {
    price: number;
    zip?: string;
    property_type?: string;
    condition?: string;
  }): Promise<BuyerMatchResponse> {
    return this.request('/api/buyers/match', {
      method: 'POST',
      body: JSON.stringify(property),
    });
  }

  async getBuyerStats(): Promise<BuyerStats> {
    return this.request('/api/buyers/stats');
  }

  // Crawl history and stats (D1 persistent)
  async getCrawlHistory(market?: string, limit = 20): Promise<CrawlHistoryResponse> {
    const params = new URLSearchParams();
    if (market) params.set('market', market);
    params.set('limit', String(limit));
    return this.request(`/api/crawl/history?${params.toString()}`);
  }

  async getCrawlStats(): Promise<CrawlStatsResponse> {
    return this.request('/api/crawl/stats');
  }

  // Data sources and refresh
  async getDataSources(): Promise<DataSourcesResponse> {
    return this.request('/api/data/sources');
  }

  async triggerDataRefresh(): Promise<DataRefreshTriggerResponse> {
    return this.request('/api/data/refresh', { method: 'POST' });
  }

  async getDataRefreshStatus(): Promise<DataRefreshStatusResponse> {
    return this.request('/api/data/refresh/status');
  }

  async getMarketTrends(market: string, days = 90): Promise<MarketTrendsResponse> {
    return this.request(`/api/data/trends?market=${market}&days=${days}`);
  }

  // Market data status and refresh
  async getMarketDataStatus(): Promise<MarketDataStatusResponse> {
    return this.request('/api/market-data/status');
  }

  async refreshRentCast(params: { market?: string; zips?: string[]; token?: string }): Promise<MarketDataRefreshResponse> {
    return this.request('/api/market-data/refresh', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async refreshZumper(params: { markets?: string[] }): Promise<MarketDataRefreshResponse> {
    return this.request('/api/market-data/refresh/zumper', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async compareMarkets(markets: string[]): Promise<MarketComparisonResponse> {
    return this.request(`/api/markets/compare?markets=${markets.join(',')}`);
  }

  // Reports
  async getReportIndex(): Promise<ReportIndexResponse> {
    return this.request('/api/reports');
  }

  async getMarketReport(market: string): Promise<MarketReportResponse> {
    return this.request(`/api/reports/${market}`);
  }

  // Rentals
  async getRentalAnalytics(params: { zipCode?: string; market?: string }): Promise<RentalAnalyticsResponse> {
    const queryParams = new URLSearchParams();
    if (params.zipCode) queryParams.set('zipCode', params.zipCode);
    if (params.market) queryParams.set('market', params.market);
    return this.request(`/api/rentals/analytics?${queryParams.toString()}`);
  }

  async getRentalComps(params: { zipCode?: string; beds?: number; baths?: number }): Promise<RentalCompsResponse> {
    const queryParams = new URLSearchParams();
    if (params.zipCode) queryParams.set('zipCode', params.zipCode);
    if (params.beds) queryParams.set('beds', String(params.beds));
    if (params.baths) queryParams.set('baths', String(params.baths));
    return this.request(`/api/rentals/comps?${queryParams.toString()}`);
  }

  async getMarketRentals(market: string): Promise<MarketRentalsResponse> {
    return this.request(`/api/rentals/markets/${market}`);
  }

  // Skip trace (BatchData - paid, on-demand)
  async skipTrace(address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  }): Promise<SkipTraceResult> {
    return this.request('/api/skiptrace', {
      method: 'POST',
      body: JSON.stringify(address),
    });
  }

  // Property history (GAP-017)
  async getPropertyHistory(propertyId: string): Promise<PropertyHistoryResponse> {
    return this.request(`/api/properties/${propertyId}/history`);
  }

  async lookupPropertyHistory(address: string, market: string): Promise<PropertyLookupResponse> {
    const params = new URLSearchParams({ address, market });
    return this.request(`/api/properties/lookup?${params.toString()}`);
  }

  // DealCheck API crawl (new data source)
  async triggerDealCheckCrawl(params: DealCheckCrawlParams): Promise<DealCheckCrawlResponse> {
    return this.request('/api/crawl/dealcheck', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // ==========================================================================
  // Market Data (RentCast)
  // ==========================================================================

  async getMarketDataStates(): Promise<MarketDataStatesResponse> {
    return this.request('/api/market-data/states');
  }

  async getMarketDataByState(stateCode: string): Promise<MarketDataStateResponse> {
    return this.request(`/api/market-data/state/${stateCode}`);
  }

  async getMarketDataByMarket(marketSlug: string): Promise<MarketDataMarketResponse> {
    return this.request(`/api/market-data/market/${marketSlug}`);
  }

  async getMarketDataByZip(zipCode: string): Promise<MarketDataZipResponse> {
    return this.request(`/api/market-data/zip/${zipCode}`);
  }

  async getMarketDataFreshness(): Promise<MarketDataFreshnessResponse> {
    return this.request('/api/market-data/freshness');
  }

  async refreshMarketData(params: { market?: string; zips?: string[]; token?: string }): Promise<MarketDataRefreshResponse> {
    return this.request('/api/market-data/refresh', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // ==========================================================================
  // Market Analysis Hierarchy (State → Market → ZIP)
  // ==========================================================================

  async getMarketAnalysisHierarchy(): Promise<MarketAnalysisHierarchyResponse> {
    return this.request('/api/market-analysis/hierarchy');
  }

  // ==========================================================================
  // Zumper Data API
  // ==========================================================================

  async getZumperMarkets(): Promise<ZumperMarketsResponse> {
    return this.request('/api/zumper/markets');
  }

  async getZumperMarket(marketSlug: string): Promise<ZumperMarketData> {
    return this.request(`/api/zumper/market/${marketSlug}`);
  }

  async getZumperMarketsBatch(marketSlugs: string[]): Promise<Record<string, ZumperMarketData>> {
    return this.request('/api/zumper/markets/batch', {
      method: 'POST',
      body: JSON.stringify({ markets: marketSlugs }),
    });
  }

  // ==========================================================================
  // Zillow Rental Data API
  // ==========================================================================

  async getZillowMarkets(): Promise<ZillowMarketsResponse> {
    return this.request('/api/zillow/markets');
  }

  async getZillowMarket(marketSlug: string): Promise<ZillowRentalData> {
    return this.request(`/api/zillow/market/${marketSlug}`);
  }

  async getZillowMarketsBatch(marketSlugs: string[]): Promise<Record<string, ZillowRentalData>> {
    return this.request('/api/zillow/markets/batch', {
      method: 'POST',
      body: JSON.stringify({ markets: marketSlugs }),
    });
  }

  // Zillow Segment APIs (expanded data with histogram & bedroom rents)
  async getZillowSegments(marketSlug: string): Promise<ZillowSegmentsResponse> {
    return this.request(`/api/zillow/segments/${marketSlug}`);
  }

  async getZillowHistory(marketSlug: string, days = 90): Promise<ZillowHistoryResponse> {
    return this.request(`/api/zillow/history/${marketSlug}?days=${days}`);
  }

  async getZillowNearby(marketSlug: string): Promise<ZillowNearbyResponse> {
    return this.request(`/api/zillow/nearby/${marketSlug}`);
  }

  // ==========================================================================
  // Historical Data API
  // ==========================================================================

  async getHistoricalRentals(params: {
    market?: string;
    region?: string;
    startDate?: string;
    endDate?: string;
    bedrooms?: number;
    limit?: number;
  }): Promise<HistoricalRentalsResponse> {
    const queryParams = new URLSearchParams();
    if (params.market) queryParams.set('market', params.market);
    if (params.region) queryParams.set('region', params.region);
    if (params.startDate) queryParams.set('startDate', params.startDate);
    if (params.endDate) queryParams.set('endDate', params.endDate);
    if (params.bedrooms !== undefined) queryParams.set('bedrooms', String(params.bedrooms));
    if (params.limit) queryParams.set('limit', String(params.limit));
    return this.request(`/api/history/rentals?${queryParams.toString()}`);
  }

  async getHistoricalHousing(params: {
    market?: string;
    region?: string;
    startDate?: string;
    endDate?: string;
    period?: 'monthly' | 'weekly';
    limit?: number;
  }): Promise<HistoricalHousingResponse> {
    const queryParams = new URLSearchParams();
    if (params.market) queryParams.set('market', params.market);
    if (params.region) queryParams.set('region', params.region);
    if (params.startDate) queryParams.set('startDate', params.startDate);
    if (params.endDate) queryParams.set('endDate', params.endDate);
    if (params.period) queryParams.set('period', params.period);
    if (params.limit) queryParams.set('limit', String(params.limit));
    return this.request(`/api/history/housing?${queryParams.toString()}`);
  }

  async getMarketHistoricalTrends(market: string, params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<MarketHistoricalTrendsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);
    const query = queryParams.toString();
    return this.request(`/api/history/trends/${market}${query ? `?${query}` : ''}`);
  }

  async getHistoricalRegions(): Promise<HistoricalRegionsResponse> {
    return this.request('/api/history/regions');
  }

  // ==========================================================================
  // Batch Crawl Status (Zillow + RentCast)
  // ==========================================================================

  async getBatchCrawlStatus(): Promise<BatchCrawlStatusResponse> {
    return this.request('/api/data/crawl-status');
  }

  // ==========================================================================
  // Data Source Crawl Triggers
  // ==========================================================================

  async triggerRoofstockCrawl(params: {
    state?: string;
    reset?: boolean;
  }): Promise<CrawlTriggerGenericResponse> {
    return this.request('/api/crawl/roofstock', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async triggerRentCastCrawl(params: {
    bearerToken: string;
    limit?: number;
    retryErrors?: boolean;
  }): Promise<CrawlTriggerGenericResponse> {
    return this.request('/api/crawl/rentcast', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async triggerZillowCrawl(params: {
    limit?: number;
    retryErrors?: boolean;
  }): Promise<CrawlTriggerGenericResponse> {
    return this.request('/api/crawl/zillow', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async triggerZumperCrawl(params: {
    limit?: number;
    retryErrors?: boolean;
  }): Promise<CrawlTriggerGenericResponse> {
    return this.request('/api/crawl/zumper', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }
}

// Crawl trigger response type
export interface CrawlTriggerGenericResponse {
  success: boolean;
  message: string;
  started?: boolean;
  jobId?: string;
}

// Batch crawl status types
export interface BatchCrawlStatusResponse {
  zillow: {
    pending?: number;
    success?: number;
    completed?: number;
    error?: number;
    no_data?: number;
    total: number;
    progress: number;
    failed?: number;
  };
  rentcast: {
    pending?: number;
    success?: number;
    completed?: number;
    error?: number;
    no_data?: number;
    total: number;
    progress: number;
    failed?: number;
  };
  roofstock?: {
    pending?: number;
    success?: number;
    completed?: number;
    subdivided?: number;
    maxed?: number;
    error?: number;
    total: number;
    progress: number;
    properties: number;
    failed?: number;
  };
  zumper?: {
    pending?: number;
    success?: number;
    completed?: number;
    error?: number;
    total: number;
    progress: number;
    failed?: number;
  };
  updatedAt: string;
}

// Market Data Status types (for refresh UI)
export interface MarketDataR2Source {
  lastUpdated: string | null;
  available: boolean;
}

export interface MarketDataStatusResponse {
  r2Sources: {
    rentcast: MarketDataR2Source;
    zumper: MarketDataR2Source;
    zillow: MarketDataR2Source;
  };
  database: {
    lastUpdated: string | null;
    zipCount: number;
  } | null;
  checkedAt: string;
}

// Market Data types
export interface MarketDataZip {
  zip_code: string;
  market?: string;
  state_code?: string;
  city?: string;
  median_rent?: number;
  avg_rent?: number;
  // Rent by bedroom (1-8BR)
  rent_1br?: number;
  rent_2br?: number;
  rent_3br?: number;
  rent_4br?: number;
  rent_5br?: number;
  rent_6br?: number;
  rent_7br?: number;
  rent_8br?: number;
  // Listings by bedroom (1-8BR)
  listings_1br?: number;
  listings_2br?: number;
  listings_3br?: number;
  listings_4br?: number;
  listings_5br?: number;
  listings_6br?: number;
  listings_7br?: number;
  listings_8br?: number;
  single_family_rent?: number;
  rent_per_sqft?: number;
  days_on_market?: number;
  total_listings?: number;
  new_listings?: number;
  rent_change_3mo?: number;
  rent_change_6mo?: number;
  rent_change_12mo?: number;
  rent_change_24mo?: number;
  listings_change_12mo?: number;
  rent_growth_pct?: number;
  report_url?: string;
  updated_at?: string;
  // Property counts (computed from properties table)
  property_count?: number;
  props_1br?: number;
  props_2br?: number;
  props_3br?: number;
  props_4br?: number;
  props_5br?: number;
  props_6br_plus?: number;
  // Population data (from population_data table)
  population?: number;
  households?: number;
  pop_median_income?: number;
  // Scarcity metrics (computed)
  props_per_1k_pop?: number;
  rentals_per_1k_pop?: number;
  scarcity_score?: number;
  scarcity_level?: 'low' | 'moderate' | 'high' | 'critical';
  // Demand metrics (from metro_demand_data via mapping)
  vacancy_rate?: number;
  vacancy_date?: string;
  metro_time_on_market?: number;
  demand_score?: number;
  demand_level?: 'high' | 'moderate' | 'low' | 'weak';
}

export interface MarketDataStatesResponse {
  states: Array<{
    stateCode: string;
    marketCount: number;
    zipCount: number;
    avgMedianRent: number;
    avgRentGrowth: number;
  }>;
  freshness: {
    lastUpdated: string | null;
    zipCount: number;
  };
}

export interface MarketDataStateResponse {
  stateCode: string;
  markets: string[];
  zipCount: number;
  avgMedianRent: number;
  avgRentGrowth: number;
  avgDaysOnMarket: number;
  totalListings: number;
  lastUpdated: string | null;
}

export interface MarketDataMarketResponse {
  market: string;
  zipCount: number;
  avgMedianRent: number;
  avgRentGrowth: number;
  avgDaysOnMarket: number;
  totalListings: number;
  lastUpdated: string | null;
  zips: MarketDataZip[];
}

export interface MarketDataZipResponse extends MarketDataZip {}

export interface MarketDataFreshnessResponse {
  lastUpdated: string | null;
  zipCount: number;
}

export interface MarketDataRefreshResponse {
  success: boolean;
  // RentCast fields
  zipsRefreshed?: number;
  zipsFailed?: number;
  market?: string | null;
  // Zumper fields
  marketsRefreshed?: number;
  marketsFailed?: number;
  r2Stored?: number;
  // Common
  errors: string[];
}

// Market Analysis Hierarchy types
export interface StateAnalysis {
  state: string;
  market_count: number;
  zip_count: number;
  property_count: number;
  avg_price: number;
  avg_score: number;
  avg_rent: number;
  avg_rent_growth: number;
  // Demand data (averaged across metros in state)
  demand_score: number;
  demand_level: string;
  // Bedroom breakdown (aggregated from all markets)
  avg_rent_1br: number;
  avg_rent_2br: number;
  avg_rent_3br: number;
  avg_rent_4br: number;
  avg_rent_5br: number;
  avg_rent_6br: number;
  avg_rent_7br: number;
  avg_rent_8br: number;
  listings_1br: number;
  listings_2br: number;
  listings_3br: number;
  listings_4br: number;
  listings_5br: number;
  listings_6br: number;
  listings_7br: number;
  listings_8br: number;
}

export interface MarketAnalysis {
  market: string;
  market_slug: string;
  state: string;
  zip_count: number;
  property_count: number;
  avg_price: number;
  avg_score: number;
  avg_rent: number;
  avg_rent_growth: number;
  avg_dom: number;
  vacancy_rate: number;
  market_temp: string;
  total_listings: number;
  // Demand data
  demand_score: number;
  demand_level: string;
  // Bedroom breakdown (aggregated from all ZIPs)
  avg_rent_1br: number;
  avg_rent_2br: number;
  avg_rent_3br: number;
  avg_rent_4br: number;
  avg_rent_5br: number;
  avg_rent_6br: number;
  avg_rent_7br: number;
  avg_rent_8br: number;
  listings_1br: number;
  listings_2br: number;
  listings_3br: number;
  listings_4br: number;
  listings_5br: number;
  listings_6br: number;
  listings_7br: number;
  listings_8br: number;
  // Property counts by bedroom
  props_1br: number;
  props_2br: number;
  props_3br: number;
  props_4br: number;
  props_5br: number;
  props_6br_plus: number;
}

export interface MarketAnalysisHierarchyResponse {
  states: StateAnalysis[];
  markets: MarketAnalysis[];
}

// DealCheck crawl types
export interface DealCheckCrawlParams {
  latitude: number;
  longitude: number;
  radius?: number;           // Miles (default: 10)
  market?: string;           // Optional market name for storage
  minPrice?: number;
  maxPrice?: number;
  minBeds?: number;
  maxBeds?: number;
  maxDaysOnMarket?: number;
  propertyTypes?: string[];
  authToken?: string;        // Firebase JWT token (optional)
}

export interface DealCheckCrawlResponse {
  success: boolean;
  source: 'dealcheck';
  market: string;
  latitude: number;
  longitude: number;
  radius: number;
  properties_found: number;
  more_available: boolean;
  stored_at: {
    r2: string;
    d1: string;
  };
  message: string;
}

// Crawl history types
export interface CrawlJob {
  id: string;
  market: string;
  budget: number;
  crawl_type?: 'properties' | 'market_data' | 'full';
  max_zips?: number;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  created_at: string;
  started_at?: string;
  completed_at?: string;
  progress?: number;
  message?: string;
  properties_found?: number;
  rental_count?: number;
  error_message?: string;
  r2_result_key?: string;
  results?: {
    properties_found?: number;
    report_url?: string;
  };
}

export interface CrawlHistoryResponse {
  jobs: CrawlJob[];
  count: number;
  market?: string;
}

export interface CrawlStatsResponse {
  overall: {
    total: number;
    completed: number;
    failed: number;
    pending: number;
    avgPropertiesFound: number;
  };
  by_market: Array<{
    market: string;
    total_jobs: number;
    completed: number;
    failed: number;
    avg_properties: number;
    last_crawl: string | null;
  }>;
}

// Data source types
export interface DataSource {
  id: string;
  name: string;
  lastUpdated: string | null;
  isStale: boolean;
  staleDays: number;
}

export interface DataSourcesResponse {
  sources: DataSource[];
  staleCount: number;
  checkedAt: string;
}

export interface DataRefreshTriggerResponse {
  success: boolean;
  message: string;
  job_id: string;
  market: string;
  budget: number;
  crawlType: string;
  status: string;
}

export interface DataRefreshStatusResponse {
  isRefreshing: boolean;
  activeJobs: CrawlJob[];
  githubWorkflow: string;
}

// Market trends types
export interface MarketTrend {
  id: number;
  market: string;
  recorded_at: string;
  avg_investment_score?: number;
  median_investment_score?: number;
  top_score?: number;
  median_price?: number;
  avg_price?: number;
  min_price?: number;
  max_price?: number;
  avg_cap_rate?: number;
  avg_cash_flow?: number;
  avg_coc?: number;
  property_count?: number;
  avg_dom?: number;
  avg_quality_score?: number;
  avg_area_score?: number;
  red_flag_pct?: number;
}

export interface MarketTrendsResponse {
  market: string;
  days: number;
  trends: MarketTrend[];
  count: number;
}

export interface ComparisonMarket {
  slug: string;
  name: string;
  state: string | null;
  landlord_score: number;
  median_price: number;
  median_rent: number;
  gross_yield: number;
  rent_to_price: string | null;
  appreciation_1yr: number | null;
  appreciation_3yr: number | null;
  appreciation_5yr: number | null;
  days_to_pending: number | null;
  price_cut_pct: number | null;
  market_heat_index: number | null;
  rental_listings: number | null;
  avg_rent_per_sqft: number | null;
  rents_by_bedroom: Record<number, number> | null;
  rentals_updated: string | null;
}

export interface MarketComparisonResponse {
  markets: ComparisonMarket[];
  errors?: Array<{ slug: string; error: string }>;
  comparison_metrics: string[];
}

// Reports types
export interface ReportIndexResponse {
  markets: Array<{
    market: string;
    name: string;
    state_code: string;
    property_count: number;
    last_updated: string;
  }>;
}

export interface MarketReportResponse {
  market: string;
  name: string;
  generated_at: string;
  summary: {
    total_properties: number;
    avg_price: number;
    avg_score: number;
    top_zips: string[];
  };
  properties: unknown[];
}

// Rentals types
export interface RentalAnalyticsResponse {
  zipCode?: string;
  market?: string;
  medianRent: number;
  avgRent: number;
  rentRange: { min: number; max: number };
  byBedroom: Record<number, { count: number; avgRent: number }>;
  totalListings: number;
}

export interface RentalComp {
  address: string;
  rent: number;
  beds: number;
  baths: number;
  sqft?: number;
  distance?: number;
  daysOnMarket?: number;
}

export interface RentalCompsResponse {
  zipCode: string;
  comps: RentalComp[];
  count: number;
  avgRent: number;
}

export interface MarketRentalsResponse {
  market: string;
  listings: RentalComp[];
  analytics: RentalAnalyticsResponse;
  saturation?: {
    score: number;
    level: string;
    perThousandPop: number;
  };
}

// Skip trace result type
export interface SkipTraceResult {
  street: string;
  city: string;
  state: string;
  zip: string;
  county?: string;
  ownerName: string;
  ownerFirst?: string;
  ownerLast?: string;
  phoneNumbers: Array<{
    number: string;
    type: string;
    carrier?: string;
    score: number;
    reachable: boolean;
    dnc: boolean;
    lastReportedDate?: string;
  }>;
  emails: string[];
  mailingStreet?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;
  isAbsentee: boolean;
  isDeceased: boolean;
  hasBankruptcy: boolean;
  hasLien: boolean;
  isLitigator: boolean;
  onDncList: boolean;
  matched: boolean;
}

// Property history types (GAP-017)
export interface PropertySnapshot {
  id?: number;
  property_id: string;
  address: string;
  market: string;
  listing_id?: string;
  zip_code?: string;
  crawl_date: string;
  price: number;
  status?: string;
  days_on_market?: number;
  investment_score?: number;
  self_score?: number;
  first_seen: string;
  last_seen: string;
}

export interface PropertyPricePoint {
  date: string;
  price: number;
  status: string;
}

export interface PropertyHistoryResponse {
  propertyId: string;
  address: string;
  market: string;
  firstSeen: string;
  lastSeen: string;
  snapshots: PropertySnapshot[];
  priceHistory: PropertyPricePoint[];
}

export interface PropertyLookupResponse {
  propertyId: string;
  snapshot: PropertySnapshot;
  priceHistory: PropertyPricePoint[];
}

// ==========================================================================
// Zumper Data Types
// ==========================================================================

export interface ZumperMarketData {
  city_slug: string;
  city_name?: string;
  state_code?: string;
  // Rent by bedroom
  median_rent_1br?: number;
  median_rent_2br?: number;
  median_rent_3br?: number;
  median_rent_4br?: number;
  avg_rent?: number;
  // YoY changes
  yoy_change_1br?: number;
  yoy_change_2br?: number;
  yoy_change_3br?: number;
  // Inventory
  listings_count?: number;
  avg_sqft?: number;
  // Demographics
  population?: number;
  renter_pct?: number;
  updated_at?: string;
  // Optional market slug (when fetched via batch)
  market_slug?: string;
}

export interface ZumperMarketsResponse {
  markets: ZumperMarketData[];
}

// ==========================================================================
// Zillow Rental Data Types
// ==========================================================================

export interface ZillowRentalData {
  resource_id: string;
  area_name?: string;
  area_type?: string;
  // Summary metrics
  median_rent?: number;
  monthly_change?: number;    // Month-over-month %
  yearly_change?: number;     // Year-over-year %
  avg_days_on_market?: number; // Zillow's DOM metric
  available_rentals?: number;
  // Market temperature
  market_temperature?: string; // 'hot', 'warm', 'cool', etc.
  // Rent range
  rent_min?: number;
  rent_max?: number;
  // Rent by bedroom (from FAQ section)
  rent_studio?: number;
  rent_1br?: number;
  rent_2br?: number;
  rent_3br?: number;
  rent_4br?: number;
  updated_at?: string;
  // Optional market slug (when fetched via batch)
  market_slug?: string;
}

export interface ZillowMarketsResponse {
  markets: ZillowRentalData[];
}

// ==========================================================================
// Zillow Segment Data Types (Expanded)
// ==========================================================================

export interface RentHistogramPoint {
  price: number;
  count: number;
}

export interface RentTrendPoint {
  month: number;
  year: number;
  price: number;
}

export interface ZillowSegment {
  resource_id: string;
  beds?: number | null;
  property_type?: string | null;
  median_rent?: number;
  monthly_change?: number;
  yearly_change?: number;
  avg_days_on_market?: number;
  available_rentals?: number;
  market_temperature?: string;
  rent_min?: number;
  rent_max?: number;
  rent_histogram?: RentHistogramPoint[];
  rent_trend_current?: RentTrendPoint[];
  rent_trend_prev_year?: RentTrendPoint[];
  bedroom_rents?: Record<string, number>;
  fetched_at?: string;
}

export interface ZillowSegmentsResponse {
  market: string;
  segments: ZillowSegment[];
}

export interface ZillowHistoryPoint {
  resource_id: string;
  beds?: number | null;
  property_type?: string | null;
  recorded_date: string;
  median_rent?: number;
  available_rentals?: number;
  avg_days_on_market?: number;
  market_temperature?: string;
}

export interface ZillowHistoryResponse {
  market: string;
  days: number;
  history: ZillowHistoryPoint[];
}

export interface ZillowNearbyMarket {
  resource_id: string;
  nearby_resource_id: string;
  nearby_area_name?: string;
  median_rent?: number;
  rent_trend?: RentTrendPoint[];
  fetched_at?: string;
}

export interface ZillowNearbyResponse {
  market: string;
  nearby: ZillowNearbyMarket[];
}

// ==========================================================================
// Historical Data Types
// ==========================================================================

export interface ZillowRentalHistoryRow {
  id: number;
  region_name: string;
  cbsa_code?: string;
  period_end: string;
  bedrooms?: number;
  median_asking_rent?: number;
  median_asking_rent_mom?: number;
  median_asking_rent_yoy?: number;
  median_asking_rent_ppsf?: number;
  count_new_listings?: number;
}

export interface RedfinMarketHistoryRow {
  id: number;
  region_name: string;
  region_type: string;
  period_type: string;
  period_start: string;
  period_end?: string;
  active_listings?: number;
  months_of_supply?: number;
  median_dom?: number;
  median_list_price?: number;
  median_sale_price?: number;
  homes_sold?: number;
  new_listings?: number;
  price_drops_pct?: number;
  sale_to_list_pct?: number;
  active_listings_yoy?: number;
  median_sale_price_yoy?: number;
}

export interface HistoricalRentalsResponse {
  source: 'zillow';
  count: number;
  data: ZillowRentalHistoryRow[];
}

export interface HistoricalHousingResponse {
  source: 'redfin';
  periodType: 'monthly' | 'weekly';
  count: number;
  data: RedfinMarketHistoryRow[];
}

export interface MarketHistoricalTrendsResponse {
  market: string;
  regionName?: string;
  zillow: {
    count: number;
    data: ZillowRentalHistoryRow[];
  };
  redfin: {
    count: number;
    data: RedfinMarketHistoryRow[];
  };
}

export interface HistoricalRegionSummary {
  region: string;
  records: number;
  firstDate: string;
  lastDate: string;
}

export interface HistoricalRegionsResponse {
  zillow: HistoricalRegionSummary[];
  redfin: HistoricalRegionSummary[];
}

// Export singleton instance
export const api = new PropertyAPI();

// Export class for custom instances
export { PropertyAPI };
